import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MasterMilestoneFindInterface } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';
import {
  MasterMilestoneFormDto,
  type MasterMilestoneFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ProjectMasterMilestoneService } from '@zaoblako/web/core/services/project/project-master-milestone.service';
import { DirectoryCommonHistoryComponent } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/directory-common-history.component';
import { DirectoryCommonHistoryTypeEnum } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/enums/directory-common-history-type.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ColorPickerComponent } from '@zaoblako/web/shared/dumbs/color-picker/color-picker.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-project-master-milestone-form',
  templateUrl: './project-master-milestone-form.component.html',
  styleUrls: ['./project-master-milestone-form.component.scss'],
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    FormsModule,
    ButtonComponent,
    SpinnerComponent,
    DirectoryCommonHistoryComponent,
    ColorPickerComponent,
    TagModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectMasterMilestoneFormComponent
  extends BaseFormComponent<MasterMilestoneFormInterface>
  implements OnInit {
  @Input() public readonly index: number;
  public masterMilestone = <MasterMilestoneFindInterface>{};
  public dto = MasterMilestoneFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public get DirectoryCommonHistoryTypeEnum() {
    return DirectoryCommonHistoryTypeEnum;
  }

  public constructor(
    public readonly projectMasterMilestoneService: ProjectMasterMilestoneService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.projectMasterMilestoneService
      .findById<MasterMilestoneFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, {
        withDeleted: true
      })
      .subscribe({
        next: (masterMilestone) => {
          this.masterMilestone = masterMilestone;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveMasterMilestone() {
    this.normalizeMasterMilestoneColor();
    if (this.checkValidate(this.masterMilestone).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.projectMasterMilestoneService
          .update<MasterMilestoneFormInterface, MasterMilestoneFindInterface>(this.masterMilestone)
          .subscribe({
            next: (masterMilestone) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Эпик "${this.masterMilestone.name}" обновлен`);
              this.formStoreService.saveNext(masterMilestone, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.projectMasterMilestoneService.create<MasterMilestoneFindInterface>(this.masterMilestone).subscribe({
          next: (masterMilestone) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Эпик создан');
            this.formStoreService.saveNext(masterMilestone, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteMasterMilestone() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить эпик "${this.masterMilestone.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.projectMasterMilestoneService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Эпик "${this.masterMilestone.name}" удален`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public restoreMasterMilestone() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить эпик "${this.masterMilestone.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.projectMasterMilestoneService
          .restoreOne(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: (masterMilestone) => {
              this.masterMilestone = masterMilestone;
              this.cdRef.markForCheck();
              this.errorService.addSuccessMessage(`Эпик "${this.masterMilestone.name}" восстановлен`);
              this.formStoreService.saveNext(masterMilestone, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public changeColor(color: string | null | undefined): void {
    const value = color?.trim() ?? '';
    if (!value) {
      this.masterMilestone.color = null;
      this.cdRef.markForCheck();
      return;
    }

    this.masterMilestone.color = value[0] !== '#' ? `#${value}` : value;
    this.cdRef.markForCheck();
  }

  public onColorPickerChange(color: string): void {
    const value = color?.trim() ?? '';
    if (!value) {
      this.masterMilestone.color = null;
    } else {
      this.masterMilestone.color = value[0] !== '#' ? `#${value}` : value;
    }
    this.cdRef.markForCheck();
  }

  private normalizeMasterMilestoneColor(): void {
    const trimmed = this.masterMilestone.color?.trim();
    if (!trimmed) {
      this.masterMilestone.color = null;
      return;
    }

    this.masterMilestone.color = trimmed[0] !== '#' ? `#${trimmed}` : trimmed;
  }
}
